import { isRestoreOperation } from 'shared/domain/documentation/operationTypeGuards';
import {
  DocumentationOperationPreflightResponse,
  DocumentationOperationType,
  SelectedItemBreakdown,
} from 'shared/domain/documentation/operationPreflight';
import {
  collectDeletedDirectoryContents,
  collectDirectoryContents,
  collectDeletedDirectoryContentsPerFolder,
  collectDirectoryContentsPerFolder,
} from './collectDirectoryContents';
import { getByIds as getDirectoriesByLocalIds } from 'serviceWorker/db/directories/directories';
import { getByIds as getDocumentationsByLocalIds } from 'serviceWorker/db/documentations/documentations';

function toSortedUniqueIds(values: number[]): number[] {
  return Array.from(new Set(values)).sort((a, b) => a - b);
}

function createSignature(
  operationType: DocumentationOperationType,
  documentations: number[],
  directories: number[]
): string {
  return [
    operationType,
    `d:${documentations.join(',')}`,
    `r:${directories.join(',')}`,
  ].join('|');
}

async function getResolvedIds(
  operationType: DocumentationOperationType,
  documentations: number[],
  directories: number[]
): Promise<{ documentations: number[]; directories: number[] }> {
  const nestedContents =
    isRestoreOperation(operationType)
      ? await collectDeletedDirectoryContents(directories)
      : await collectDirectoryContents(directories);

  return {
    documentations: toSortedUniqueIds([
      ...documentations,
      ...nestedContents.documentationLocalIds,
    ]),
    directories: toSortedUniqueIds([
      ...directories,
      ...nestedContents.directoryLocalIds,
    ]),
  };
}

async function buildSelectedItemsBreakdown(
  operationType: DocumentationOperationType,
  documentationLocalIds: number[],
  directoryLocalIds: number[]
): Promise<SelectedItemBreakdown[]> {
  const breakdown: SelectedItemBreakdown[] = [];

  // Get per-folder nested counts
  const perFolderCounts =
    isRestoreOperation(operationType)
      ? await collectDeletedDirectoryContentsPerFolder(directoryLocalIds)
      : await collectDirectoryContentsPerFolder(directoryLocalIds);

  // Fetch directory entities to get names
  if (directoryLocalIds.length > 0) {
    const directories = await getDirectoriesByLocalIds(directoryLocalIds);
    for (const dir of directories) {
      breakdown.push({
        type: 'directory',
        localId: dir.localId,
        name: dir.name,
        nestedCount: perFolderCounts[dir.localId] ?? 0,
      });
    }
  }

  // Fetch documentation entities to get names
  if (documentationLocalIds.length > 0) {
    const documentations = await getDocumentationsByLocalIds(documentationLocalIds);
    for (const doc of documentations) {
      breakdown.push({
        type: 'file',
        localId: doc.localId,
        name: doc.name,
        nestedCount: 0,
      });
    }
  }

  return breakdown;
}

export async function calculateDocumentationOperationPreflight(params: {
  operationType: DocumentationOperationType;
  documentations: number[];
  directories: number[];
}): Promise<DocumentationOperationPreflightResponse> {
  const [resolved, selectedItemsBreakdown] = await Promise.all([
    getResolvedIds(
      params.operationType,
      params.documentations,
      params.directories
    ),
    buildSelectedItemsBreakdown(
      params.operationType,
      params.documentations,
      params.directories
    ),
  ]);

  return {
    operationType: params.operationType,
    resolvedDocumentations: resolved.documentations,
    resolvedDirectories: resolved.directories,
    resolvedCount: resolved.documentations.length + resolved.directories.length,
    signature: createSignature(
      params.operationType,
      resolved.documentations,
      resolved.directories
    ),
    selectedItemsBreakdown,
  };
}
